import { useEffect, useState } from 'react';
import 'react-popper-tooltip/dist/styles.css';

import { CLICK_IDS } from '@sunflower-games/analytics-events';
import classNames from 'classnames';
import { observer } from 'mobx-react';
import { useTranslation } from 'react-i18next';
import { usePopperTooltip } from 'react-popper-tooltip';

import Button from '../../../components/buttons/Button';
import { DialogLoader } from '../../../components/Dialog/DialogLoader';
import { ErrorMessage } from '../../../components/ErrorMessage/ErrorMessage';
import { InfoIcon } from '../../../components/SVGIcons/InfoIcon';
import { useMarketingConsents } from '../../../hooks/useMarketingConsents';
import { analytics } from '../../../services/analytics';
import { useStores } from '../../../stores/useStores';
import { checkIfAppleDevice } from '../../../utils/helpers';
import { isInAppBrowser } from '../../../utils/isInAppBrowser';
import { useHandleAuth } from '../useHandleAuth';
import { useHandleSignUpWizard } from '../wizards/useHandleSIgnUpWizard';

import appleIcon from './appleIcon.svg';
import facebookIcon from './facebookIcon.svg';
import googleIcon from './googleIcon.svg';
import InAppBlockedTooltip from './InAppBlockedTooltip/InAppBlockedTooltip';
import styles from './ThirdPartyAuth.module.scss';

type ThirdPartyAuthProvider = 'apple' | 'google' | 'facebook';

export const ThirdPartyAuth = observer(
  ({
    action,
    excludedProviders = [],
    onLoginAttempt,
    className,
    hideLoader = false,
  }: {
    action: 'Sign Up' | 'Log in';
    excludedProviders?: ThirdPartyAuthProvider[];
    onLoginAttempt?: () => boolean;
    className?: string;
    hideLoader?: boolean;
  }) => {
    const { authStore, portalStore, marketingConsentStore } = useStores();
    const { t } = useTranslation();

    const { isSigningUp } = authStore;
    const landingKey = authStore.getLandingKey();
    const isDirectLanding = authStore.isDirectLanding();

    const { getArrowProps, getTooltipProps, setTooltipRef, setTriggerRef, visible } = usePopperTooltip({
      trigger: 'click',
      placement: 'bottom-end',
    });

    const [error, setError] = useState<string>();

    const { handleAuth } = useHandleAuth(setError);
    const { handleSignUpWizard } = useHandleSignUpWizard();

    const isGoogleSignInBlocked = isInAppBrowser;

    const { signUpMarketingConsents, fetchingSignUpMarketingConsent } = marketingConsentStore;
    const { areAllOptionalConsentsChecked } = useMarketingConsents({
      initialMarketingConsents: signUpMarketingConsents,
      fetchingMarketingConsents: fetchingSignUpMarketingConsent,
      localStorageKey: 'signup-marketing-consents',
    });

    const handleSocialSignIn = (provider: 'apple' | 'google' | 'facebook') => () => {
      if (isGoogleSignInBlocked && provider === 'google') {
        return;
      }

      const actionFormatted = action === 'Sign Up' ? 'SIGN_UP' : 'LOG_IN';

      if (actionFormatted === 'SIGN_UP' && isDirectLanding && typeof onLoginAttempt === 'function') {
        const isValid = onLoginAttempt();

        if (!isValid) {
          return;
        }
      }

      if (actionFormatted === 'SIGN_UP') {
        handleSignUpWizard({ provider, action: 'sign_up', areAllOptionalConsentsChecked });
      } else {
        void analytics.sendEvent('log_in', {
          step_name: 'log_in_platform',
          event_action: 'click',
          click_id: CLICK_IDS.AUTH_BUTTONS[actionFormatted][provider],
          log_in_platform: provider,
          triggered_by: portalStore.options.triggeredBy as never,
          landingKey,
        });
        void handleAuth({ provider, action: 'log_in' });
      }
    };

    useEffect(() => {
      authStore.setPending('thirdPartyAuth', false);
    }, [authStore]);

    return (
      <div className={classNames(styles.thirdPartyAuth, className)}>
        {!hideLoader && <DialogLoader isProcessing={isSigningUp} />}

        {checkIfAppleDevice() && !excludedProviders.includes('apple') && (
          <Button
            btnType="light"
            className={styles.thirdPartyAuthButton}
            disabled={isSigningUp}
            iconLeft={<img alt="apple-icon" className={styles.iconImg} src={appleIcon} />}
            onClick={handleSocialSignIn('apple')}
          >
            {action === 'Sign Up' ? t('account.signUpApple') : t('account.signInApple')}
          </Button>
        )}

        {!excludedProviders.includes('google') && (
          <>
            <Button
              ref={setTriggerRef}
              btnType="light"
              className={classNames(styles.thirdPartyAuthButton, isGoogleSignInBlocked && styles.withRightIcon)}
              disabled={isSigningUp}
              iconLeft={<img alt="google-icon" className={styles.iconImg} src={googleIcon} />}
              iconRight={isGoogleSignInBlocked ? <InfoIcon color="red" /> : undefined}
              onClick={handleSocialSignIn('google')}
            >
              {action === 'Sign Up' ? t('account.signUpGoogle') : t('auth.logInWithGoogle')}
            </Button>
            {visible && isGoogleSignInBlocked && (
              <InAppBlockedTooltip
                getArrowProps={getArrowProps}
                getTooltipProps={getTooltipProps}
                setTooltipRef={setTooltipRef}
              />
            )}
          </>
        )}

        {!excludedProviders.includes('facebook') && (
          <Button
            btnType="blue"
            className={styles.thirdPartyAuthButton}
            disabled={isSigningUp}
            iconLeft={<img alt="facebook-icon" className={styles.iconImg} src={facebookIcon} />}
            onClick={handleSocialSignIn('facebook')}
          >
            {action === 'Sign Up' ? t('account.signUpFacebook') : t('account.signInFacebook')}
          </Button>
        )}

        <ErrorMessage noSpaceWhenEmpty>{error}</ErrorMessage>
      </div>
    );
  },
);
